import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  listInventoryMaps, uploadLocations, setMapCalibration, getStructureSummary,
} from "../../../api/rest";
import { AddressGrammarField } from "../../AddressGrammarField";
import { chooseGrammar } from "../../addressGrammar";
import { readAddresses } from "../../zonesFile";
import { rejectReasonLabel } from "../../rejectReasons";
import { useSiteId } from "../../../hooks/useSiteId";
import css from "../../WarehouseAdminPage.module.css";

/**
 * The address master (ADR 142): import the warehouse's storage locations and see
 * which map each landed on. The zones.json import lives here rather than under
 * /inventory — reports and the occupancy layer join at read time, so importing
 * addresses before or after an inventory upload both work (post-hoc runs link
 * via ADR 140).
 */
export function AddressesSection() {
  const { t, i18n } = useTranslation();
  const siteId = useSiteId();
  const { warehouseId = "" } = useParams();
  const qc = useQueryClient();

  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Aisle grammar (ADR 137): Java regex with a named group `aisle`; without it
  // every location lands in the "__none__" aisle bucket. ADR 228: normally built
  // by AddressGrammarField from the chosen file rather than typed.
  const [aislePattern, setAislePattern] = useState("");
  // ADR 228: the file is chosen first and parsed client-side, so the grammar can be
  // built against its REAL addresses and its match rate shown before the import runs.
  const [pending, setPending] = useState<{ file: File; addresses: string[] } | null>(null);
  const [result, setResult] = useState<{ created: number; updated: number; deactivated: number; unchanged: number; rejects: string[] } | null>(null);

  // ADR 231: the grammar this warehouse already learned. Same query key as the
  // LayerPanel's availability probe, so the two share one fetch.
  const { data: structureSummary } = useQuery({
    queryKey: ["structure-summary", siteId, warehouseId],
    queryFn: () => getStructureSummary(siteId, warehouseId),
    enabled: !!warehouseId,
    retry: 0,
  });
  const remembered = structureSummary?.addressPattern ?? null;

  // Same query key as InventoryPage's map-name datalist: an import here
  // refreshes the upload suggestions there for free.
  const { data: maps = [] } = useQuery({
    queryKey: ["inventory-maps", siteId],
    queryFn: () => listInventoryMaps(siteId),
    retry: 0,
  });
  const warehouseMaps = maps.filter((m) => m.warehouseId === warehouseId);

  // ADR 169: reset a map's calibration back to identity (renders in place). The
  // full visual calibration editor is the Mapa ▸ Alinhamento tab; clearing a bad
  // transform is the recovery valve surfaced here.
  const clearCal = useMutation({
    mutationFn: (mapId: string) => setMapCalibration(siteId, mapId, null),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inventory-maps", siteId] }),
  });

  /** Choosing a file only READS it (ADR 228) — the import waits for an explicit
   *  click, so the grammar can be built and checked against these addresses first.
   *
   *  The suggested pattern is computed HERE, in the same update as the file, not
   *  left for the field to publish on its next effect: otherwise the import button
   *  is clickable for one render while the parent still holds an empty pattern, and
   *  a quick click would import with no grammar while the panel showed a match rate. */
  async function handlePick(file: File) {
    setErr(null);
    setResult(null);
    let addresses: string[] = [];
    try {
      addresses = await readAddresses(file);
    } catch (x) {
      // Unreadable here is not fatal — the server parses it properly and reports.
      setErr(x instanceof Error ? x.message : String(x));
    }
    setPending({ file, addresses });
    const sample = addresses[0] ?? "";
    // Same rule the panel renders (ADR 231) — computed here, in the same update as
    // the file, so a fast click cannot import before the grammar reaches state.
    setAislePattern(sample ? chooseGrammar(sample, addresses, remembered).pattern : "");
  }

  async function handleUpload() {
    if (!pending) return;
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const r = await uploadLocations(siteId, warehouseId, pending.file, aislePattern.trim() || undefined);
      setResult({ created: r.created, updated: r.updated, deactivated: r.deactivated, unchanged: r.unchanged, rejects: r.rejects ?? [] });
      await qc.invalidateQueries({ queryKey: ["inventory-maps", siteId] });
      await qc.invalidateQueries({ queryKey: ["structure-summary", siteId, warehouseId] });
      setPending(null);
    } catch (x) {
      setErr(x instanceof Error ? x.message : String(x));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <>
      <p className={css.note}>{t("structure.addresses.note")}</p>

      <div className={css.form}>
        <label className={css.fileLabel}>
          {pending ? t("structure.addresses.changeFile") : t("structure.addresses.pickFile")}
          <input
            ref={fileRef}
            type="file"
            accept=".json,application/json"
            hidden
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handlePick(f);
            }}
          />
        </label>
        {pending && (
          <span className={css.note}>
            {t("structure.addresses.picked", {
              name: pending.file.name, count: pending.addresses.length,
            })}
          </span>
        )}
      </div>

      {pending && (
        <>
          <AddressGrammarField
            addresses={pending.addresses}
            value={aislePattern}
            onChange={setAislePattern}
            disabled={busy}
            remembered={remembered}
          />
          <div className={css.form}>
            <button type="button" className={css.linkBtn} disabled={busy} onClick={handleUpload}>
              {busy ? t("structure.addresses.importing") : t("structure.addresses.import")}
            </button>
          </div>
        </>
      )}

      {err && <p className={css.resultError} role="alert">{err}</p>}
      {result && (
        <div className={result.rejects.length > 0 ? css.resultWarn : css.resultOk} role="status">
          <p>
            {t("structure.addresses.done", {
              created: result.created, updated: result.updated, deactivated: result.deactivated, unchanged: result.unchanged,
            })}
          </p>
          {result.rejects.length > 0 && (
            <ul className={css.rejectList}>
              {result.rejects.map((r, i) => (
                <li key={`${r}-${i}`}>{rejectReasonLabel(r, t)}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <table className={css.table} data-testid="maps-table">
        <thead>
          <tr>
            <th>{t("structure.addresses.col.map")}</th>
            <th>{t("structure.addresses.col.locations")}</th>
            <th>{t("structure.addresses.col.frame")}</th>
          </tr>
        </thead>
        <tbody>
          {warehouseMaps.map((m) => (
            <tr key={m.id}>
              <td>{m.name}</td>
              <td>{(m.locationCount ?? 0).toLocaleString(i18n.language)}</td>
              <td>
                <span className={`${css.badge} ${m.calibrated ? css.badgeManual : css.badgeDerived}`}>
                  {t(m.calibrated ? "structure.addresses.frame.calibrated" : "structure.addresses.frame.identity")}
                </span>
                {m.calibrated && (
                  <button type="button" className={css.linkBtn} style={{ marginLeft: 8 }}
                    disabled={clearCal.isPending}
                    onClick={() => clearCal.mutate(m.id)}>
                    {t("structure.addresses.frame.clear")}
                  </button>
                )}
              </td>
            </tr>
          ))}
          {warehouseMaps.length === 0 && (
            <tr><td colSpan={3} style={{ textAlign: "center", padding: 24, color: "var(--op-ink-500)" }}>
              {t("structure.addresses.empty")}
            </td></tr>
          )}
        </tbody>
      </table>
    </>
  );
}
